import React from 'react'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { t } from 'ttag'
import { updateProgrammeFilters, updateSelectedFilters } from 'store/actions/filters'
import {
  ENPI_ENI_CROSS_BORDER,
  INTERREG_IPA_CROSS_BORDER,
  IPA_IPA_CROSS_BORDER_COOPERATION,
  OTHER_EUSBSR_RELATED,
  OTHER_EUSDR_RELATED,
  PERIOD_2000_2006,
  SELECTED_FILTER_MAX_LINES
} from 'store/constants/constants'
import Button from 'components/ui/Button'
import FilterTitle from 'components/ui/FilterTitle'
import SearchFilter from 'components/ui/SearchFilter'
import SelectedFilter from 'components/ui/SelectedFilter'
import FilterText from 'components/ui/FilterText'
import RemoveIcon from 'components/ui/RemoveIcon'
import SelectedFilterContainer from 'components/ui/SelectedFilterContainer'
import Dotdotdot from 'react-dotdotdot'
import CustomDropDownTreeSelect from 'components/ui/CustomDropdownTreeSelect'

function ProgrammeRelatedMultiSelectType({
  type,
  filterData,
  values,
  title,
  onClose,
  showFilter,
  keyWord,
  customTop
}) {
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  const filterObject = filters.selectedFilters[currentSearchable][type]
  return (
    <PureProgrammeRelatedMultiSelectType
      type={type}
      values={values}
      filterData={filterData}
      title={title}
      onClose={onClose}
      showFilter={showFilter}
      keyWord={keyWord}
      customTop={customTop}
      filterObject={filterObject}
    />
  )
}

const PureProgrammeRelatedMultiSelectType = React.memo(
  function PureProgrammeRelatedMultiSelectType({
    type,
    filterData,
    values,
    title,
    onClose,
    showFilter,
    keyWord,
    customTop,
    filterObject
  }) {
    const dispatch = useDispatch()

    const selectedValues = filterObject[values[0]]

    const options = filterData.map(option => {
      const parentOption = {
        label: option[keyWord],
        value: option.id,
        expanded: true
      }
      const selectedOption = selectedValues.filter(
        s => s.id === option.id && s.label === option[keyWord]
      )
      if (selectedOption.length > 0) {
        parentOption.checked = true
      }
      return parentOption
    })

    function setProgrammeFilters(selectedNodes) {
      const filterId = 'types'
      const programmeFilterValue = selectedNodes.map(node => node.value || node.id).join(',')
      const programmeFilterData = {
        filterId,
        filterValue: programmeFilterValue
      }
      dispatch(updateProgrammeFilters(programmeFilterData))
    }

    function checkDynamicSelections(selectedTypes) {
      const { period: selectedPeriods } = filterObject

      // Remove all selections from programmes filter since now the selected programmes might not be in available options
      const programmesData = {
        filterType: type,
        filterId: 'available',
        filterValue: []
      }
      dispatch(updateSelectedFilters(programmesData))

      let dynamicSelections = selectedPeriods
      if (
        selectedTypes.includes(INTERREG_IPA_CROSS_BORDER) ||
        selectedTypes.includes(IPA_IPA_CROSS_BORDER_COOPERATION) ||
        selectedTypes.includes(ENPI_ENI_CROSS_BORDER)
      ) {
        dynamicSelections = dynamicSelections.filter(option => option.label !== PERIOD_2000_2006)
      }
      if (
        selectedTypes.includes(OTHER_EUSDR_RELATED) ||
        selectedTypes.includes(OTHER_EUSBSR_RELATED)
      ) {
        dynamicSelections = []
      }
      const periodData = {
        filterType: type,
        filterId: 'period',
        filterValue: dynamicSelections
      }
      dispatch(updateSelectedFilters(periodData))
    }

    const onChange = (currentNode, selectedNodes) => {
      const filterValue = selectedNodes.map(node => ({
        id: node.value,
        label: node.label
      }))
      const data = {
        filterType: type,
        filterId: values[0],
        filterValue
      }
      dispatch(updateSelectedFilters(data))
      setProgrammeFilters(selectedNodes)
      checkDynamicSelections(selectedNodes.map(node => node.label))
    }

    function handleFilterRemove(filterId, filterValue, valueIsArray = false) {
      let resultingValue = null
      if (valueIsArray) {
        resultingValue = filterObject[filterId].filter(
          s => s.id !== filterValue.id || s !== filterValue
        )
      }
      const data = {
        filterType: type,
        filterId,
        filterValue: resultingValue
      }
      dispatch(updateSelectedFilters(data))
      setProgrammeFilters(resultingValue)
    }

    function renderSelectedValues() {
      return values.map(value => {
        if (filterObject[value]) {
          const filterValue = filterObject[value]
          if (filterValue instanceof Array) {
            return filterValue.map(v => (
              <SelectedFilterContainer className="selected-filters" key={v.id ? v.id : v}>
                <SelectedFilter>
                  <FilterText title={v.label ? v.label : v}>
                    <Dotdotdot clamp={SELECTED_FILTER_MAX_LINES}>{v.label ? v.label : v}</Dotdotdot>
                  </FilterText>
                  <RemoveIcon
                    className="far fa-times"
                    onClick={() => handleFilterRemove(value, v, true)}
                  />
                </SelectedFilter>
              </SelectedFilterContainer>
            ))
          }
        }
        return false
      })
    }

    return (
      <>
        <SearchFilter open={showFilter} customTop={customTop}>
          <FilterTitle>{title}</FilterTitle>
          <CustomDropDownTreeSelect data={options} onChange={onChange} />
          <Button size="medium" onClick={onClose}>
            {t`Close`}
          </Button>
        </SearchFilter>
        <div>{renderSelectedValues()}</div>
      </>
    )
  }
)

ProgrammeRelatedMultiSelectType.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string
}

PureProgrammeRelatedMultiSelectType.propTypes = {
  type: PropTypes.string,
  filterData: PropTypes.array,
  values: PropTypes.array,
  title: PropTypes.string,
  onClose: PropTypes.func,
  showFilter: PropTypes.bool,
  keyWord: PropTypes.string,
  customTop: PropTypes.string,
  filterObject: PropTypes.object
}

export default ProgrammeRelatedMultiSelectType
